import { useContext, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Divider, Link, Chip } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import { OrderConfirmationPaymentContext } from '../../context/OrderConfirmationPaymentContext';
import { MOBILE_LAYOUT_MAX_WIDTH_PX, PRODUCT_TYPES } from '../../utils/constants';
import envelopeLogo from '../../assets/logos/envelope.svg';

const OrderConfirmationLiveView = () => {
  const { state, currencyOptions } = useContext(AppContext);
  const { paymentProviders } = useContext(OrderConfirmationPaymentContext);

  const formatPrice = (price, currencyCode = 'USD') => {
    const amount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price || 0);
    const match = currencyOptions?.find((c) => c.code === currencyCode);
    const symbol = match?.symbol;
    if (symbol && String(symbol).trim() !== '') return `${symbol}${amount}`;
    return `${currencyCode} ${amount}`;
  };

  const paymentMethod = useMemo(() => {
    if (paymentProviders?.is_stripe_enabled) {
      return 'Stripe';
    }
    if (paymentProviders?.is_paypal_enabled) {
      return 'PayPal';
    }
    return '<Payment Method>';
  }, [paymentProviders]);

  const orderDate = new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '-');

  const productThumbPx = 198;
  const descriptionText = state.product.description || 'Product description';
  const isDigital = state.productType === PRODUCT_TYPES.DIGITAL;

  const mockOrderData = {
    orderNumber: 'XX-XXXXXX-XXXXXXXXX',
    orderDate,
    paymentMethod,
    billingAddress: {
      name: 'John Doe',
      street: '12586 Brady Rd',
      city: 'Jacksonville',
      state: 'Florida',
      zip: '32223',
      country: 'US',
    },
    shippingAddress: {
      name: 'John Doe',
      street: '12586 Brady Rd',
      city: 'Jacksonville',
      state: 'Florida',
      zip: '32223',
      country: 'US',
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        p: { xs: 1, sm: 1, md: 1 },
        height: { xs: 'auto', md: '100%' },
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'auto' },
      }}
    >
      <Card
        sx={{
          width: { xs: '100%', md: '60%' },
          maxWidth: '100%',
          mx: 'auto',
          flex: { xs: '0 0 auto', md: 1 },
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          backgroundColor: '#fff',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { xs: 2, md: 4 },
            overflow: { xs: 'visible', md: 'auto' },
            minHeight: 0,
          }}
        >
          <Box>
            <Box
              component="img"
              src={envelopeLogo}
              alt=""
              sx={{ display: 'block', width: { xs: 24, md: 40 }, height: { xs: 24, md: 40 }, mb: 2 }}
            />

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.95rem', md: '1.5rem' } }}>
              Your Order Confirmation
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '1rem' } }}>
              {'Hi <customer name>,'}
            </Typography>

            <Typography variant="body1" sx={{ mb: { xs: 0.5, md: 2 }, fontSize: { xs: '0.75rem', md: '1rem' } }}>
              Thank you for your order from{' '}
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {state.businessInfo.companyName || '<companyname>'}
              </Typography>
              .
            </Typography>

            {isDigital && (
              <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '1rem' } }}>
                You will receive an email with your digital download link.
              </Typography>
            )}

            <Typography variant="body1" sx={{ mb: 0, fontSize: { xs: '0.7rem', md: '1rem' } }}>
              If you have any questions about your order please contact us at{' '}
              <Link href={`mailto:${state.businessInfo.email || '<youremailaddress>'}`} underline="hover">
                {state.businessInfo.email || '<youremailaddress>'}
              </Link>
            </Typography>

            <Divider sx={{ my: { xs: 2, md: 3 } }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: { xs: 1.5, md: 3 },
                mb: 0,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                  Order Number
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.875rem' }, overflowWrap: 'anywhere' }}>{mockOrderData.orderNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                  Order Date
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.875rem' } }}>{mockOrderData.orderDate}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                  Payment Method
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.875rem' } }}>{mockOrderData.paymentMethod}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3, display: { xs: 'none', md: 'block' } }} />

            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: isDigital ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                columnGap: 4,
                rowGap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Billing Address
                </Typography>
                <Typography variant="body2">
                  {mockOrderData.billingAddress.name}
                  <br />
                  {mockOrderData.billingAddress.street}
                  <br />
                  {mockOrderData.billingAddress.city} {mockOrderData.billingAddress.state}{' '}
                  {mockOrderData.billingAddress.zip}
                  <br />
                  {mockOrderData.billingAddress.country}
                </Typography>
              </Box>
              {!isDigital && (
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Shipping Address
                  </Typography>
                  <Typography variant="body2">
                    {mockOrderData.shippingAddress.name}
                    <br />
                    {mockOrderData.shippingAddress.street}
                    <br />
                    {mockOrderData.shippingAddress.city} {mockOrderData.shippingAddress.state}{' '}
                    {mockOrderData.shippingAddress.zip}
                    <br />
                    {mockOrderData.shippingAddress.country}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3, display: { xs: 'none', md: 'block' } }} />

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'start',
                minWidth: 0,
                containerType: 'inline-size',
              }}
            >
              {state.product.imageUrl ? (
                <Box
                  component="img"
                  src={state.product.imageUrl}
                  alt={state.product.name || 'Product'}
                  sx={{
                    flex: `0 0 ${productThumbPx}px`,
                    width: productThumbPx,
                    maxWidth: '100%',
                    height: productThumbPx,
                    objectFit: 'cover',
                    borderRadius: 1,
                    alignSelf: 'start',
                    [`@container (max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px)`]: {
                      flexBasis: '100%',
                      width: '100%',
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    flex: `0 0 ${productThumbPx}px`,
                    width: productThumbPx,
                    maxWidth: '100%',
                    height: productThumbPx,
                    alignSelf: 'start',
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    [`@container (max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px)`]: {
                      flexBasis: '100%',
                      width: '100%',
                    },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Image
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  flex: '1 1 220px',
                  minWidth: 'min(100%, 220px)',
                  maxWidth: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  columnGap: 2,
                  rowGap: 0.5,
                  alignItems: 'start',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    gridColumn: 1,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    pt: 0.125,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {state.product.name || 'Product Name'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ gridColumn: 2, color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  QTY: 1
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    gridColumn: 3,
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                    lineHeight: 1.25,
                    textAlign: 'right',
                  }}
                >
                  {formatPrice(state.product.price, state.product.currency)}
                </Typography>
                <Box
                  sx={{
                    gridColumn: '1 / -1',
                    maxHeight: productThumbPx,
                    overflow: 'hidden',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {descriptionText}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                  {isDigital && (
                    <Chip
                      label="Digital Download"
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        height: 24,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        backgroundColor: 'rgba(50, 110, 234, 0.12)',
                        color: 'primary.main',
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <Divider sx={{ mt: 3 }} />
            <Box
              sx={{
                mx: -4,
                mb: -4,
                px: 4,
                py: 3,
                textAlign: 'center',
                borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
                borderBottomRightRadius: (theme) => theme.shape.borderRadius,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {`${state.businessInfo.companyName || '<companyname>'}, ${state.businessInfo.address || '<businessaddress>'}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} {state.businessInfo.companyName || '<companyname>'}. All Rights Reserved.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderConfirmationLiveView;
